import React from 'react';

import { useAppDispatch } from 'ducks/hooks';
import { useIntl } from 'react-intl';
import { useTestFeature } from 'optimizely/hooks';
import { helpLinesShape, setShape } from 'ducks/helpLines';
import { ProductType, ShapeTypeEnum } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import useIsRtl from 'hooks/useIsRtl';
import ArrowLeftIcon from '@ingka/ssr-icon/paths/arrow-left';
import ArrowRightIcon from '@ingka/ssr-icon/paths/arrow-right';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { setAutofillActive } from 'rangeConfig/platsa/features/Autofill/Autofill';
import { setAutoFillSheetVisible, setStandardFramesSheetVisible } from '../AutofillPax';
import { isMobile } from 'utils/platform';
import { useBehaviorSystem } from 'contexts/behaviors';
import { getSelectedGroupId } from 'ducks/products';

const Wrapper = styled.div`
  background: #eee;
  border-radius: 10px;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border: none;
  width: 100%;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;

  @media (min-width: 768px) {
    background: #111;
  }
`;

const Text = styled(Body)`
  color: ${({ theme }) => theme.colors.text};

  @media (min-width: 768px) {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const LinkToAutofill = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const currentShape = useSelector(helpLinesShape);
  const autofillEnabled = useTestFeature('PaxAutofill');
  const autofillMobileTestEnabled = useTestFeature('PaxAutofillMobile');
  const coreApi = useCore();
  const isRtl = useIsRtl();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const behaviorSystem = useBehaviorSystem();

  const onClick = () => {
    behaviorSystem.startReport(`autofill-pax-link-to-sheet`, {
      groupId: selectedGroupId,
      menuType: 'sheet',
      widgetId: 'autofill-pax-link-to-sheet'
    });

    const framesInScene = coreApi.entities.getByProductType(ProductType.PAX_FRAME);

    //If there are no frames in scene we want to autofill immediately
    if (framesInScene.length === 0) {
      dispatch(setAutofillActive(true));
    }
    if (currentShape === ShapeTypeEnum.off) {
      dispatch(setShape(ShapeTypeEnum.squared));
    }

    if (autofillEnabled) {
      dispatch(setAutoFillSheetVisible(true));
    }
    if (isMobile() && autofillMobileTestEnabled) {
      dispatch(setStandardFramesSheetVisible(true));
      dispatch(setAutoFillSheetVisible(false));
    }
  };

  return (
    <Wrapper onClick={onClick}>
      <Text type={ThemeFontStyleTypeEnum.bodyMedium} style={{ fontWeight: 700 }}>
        {formatMessage({
          id:
            autofillMobileTestEnabled && isMobile()
              ? 'storageone.autofillPaxLinkTextCVersion'
              : 'storageone.autofillPaxLSheetTitleBVersion'
        })}
      </Text>
      <Button
        ssrIcon={isRtl ? ArrowLeftIcon : ArrowRightIcon}
        dataTestId="pax-autofill-link"
        onClick={onClick}
        type={ButtonTypeEnum.primary}
        expanding={false}
        iconOnly={true}
        size="small"
        inverseTheme={!isMobile()}
      />
    </Wrapper>
  );
};

export default LinkToAutofill;
